import { put, select, takeLatest } from 'redux-saga/effects';
import delay from '@redux-saga/delay-p';
import {
  setScrollIntervalId,
  setStartDelayTime,
  setScrollSpeed,
  setOpenStudio,
  setRunningAudioService,
  setAutoScrolling,
  setScrollPosition,
  setAutoScrollAudioService,
  setPlayPrompter,
  setGuideStudio,
  setFontSize,
  setAverageCalibrateVolume,
  setStudioMainContainerHeight,
  setRunCountdown,
  setCurrentCountdown,
  setCountdown,
  setStopCountdown,
  setOpenSettingsPanel,
  setupStudio,
  setMediaNotAvailable,
  setPlayPrompterWithAudioCountdown,
  setRestartPrompter,
  setSetupAndTogglePrompter,
  setCameraRecording,
  setCountdownCamera,
  toggleCameraRecording,
  setRecordingAfterDelay,
  restartRecordingCamera,
} from './actions';
import {
  saveSettings, saveSettingsByUser, getSettingsByOwnerUser,
} from './utils';
import { selectProject } from '../projects/selectors';
import {
  selectScrollIntervalId,
  selectIsRunningAudioService,
  selectAutoScrollAudioService,
  selectIsPlayPrompter,
  selectIsCountdown,
  selectCurrentCountdown,
  selectStartDelayTime,
  selectIsSupportUserAudio,
  selectCameraRecording,
  selectCountdownCamera, selectTeleprompterSync, selectStudioMode, selectCameraId,
} from './selectors';
import { StudioMode } from '../../../configs/studio';

function runAudioWithCountdown() {
  window.studioAudioService?.close();
  window.studioAudioService?.startWithCountDown();
}

function* setupAndTogglePrompter() {
  const startDelay = yield select(selectStartDelayTime);
  const isPlay = yield select(selectIsPlayPrompter);
  const isAutoScrollAudioService = yield select(selectAutoScrollAudioService);

  if (startDelay) {
    if (isPlay) {
      yield put(setPlayPrompter(false));
    } else if (isAutoScrollAudioService) {
      yield runAudioWithCountdown();
    } else {
      yield put(setRunCountdown());
    }
  } else {
    yield put(setPlayPrompter(!isPlay));
  }
}

function* restartPrompter() {
  const startDelay = yield select(selectStartDelayTime);
  const isAutoScrollAudioService = yield select(selectAutoScrollAudioService);
  const isCountdown = yield select(selectIsCountdown);

  if (startDelay) {
    if (!isCountdown) {
      yield put(setRunCountdown());
      if (isAutoScrollAudioService) {
        yield window.studioAudioService?.stopProcessingVoice();
      }
    }
  } else if (!isAutoScrollAudioService) {
    yield put(setPlayPrompter(false));
    yield put(setSetupAndTogglePrompter());
  }
}

function* playPrompter() {
  try {
    const isPlay = yield select(selectIsPlayPrompter);
    const isAutoScrollAudioService = yield select(selectAutoScrollAudioService);
    const isSupportAudio = yield select(selectIsSupportUserAudio);
    const withCountdown = yield select(selectStartDelayTime);
    const isAudioServiceRunning = yield select(selectIsRunningAudioService);

    if (withCountdown && isAutoScrollAudioService) {
      yield window.studioAudioService?.stopProcessingVoice();
      yield window.studioAudioService?.startProcessingVoice();
    }
    if (isPlay) {
      if (isAutoScrollAudioService && isSupportAudio) {
        yield put(setRunningAudioService(true));
      } else {
        yield put(setAutoScrolling(true));
      }
    } else {
      yield put(setCurrentCountdown(0));
      yield put(setCountdown(false));
      yield put(setAutoScrolling(false));
      if (isAudioServiceRunning) {
        yield put(setRunningAudioService(false));
      }
    }
  } catch (e) {
    console.log(e);
  }
}

function* runCountdownAndPlay() {
  const startDelay = yield select(selectStartDelayTime);
  const isCountdown = yield select(selectIsCountdown);

  if (isCountdown) {
    yield put(setCurrentCountdown(0));
    yield put(setCountdown(false));
  } else {
    yield put(setCountdown(true));
    yield put(setCurrentCountdown(startDelay));

    while (yield select(selectIsCountdown)) {
      const current = yield select(selectCurrentCountdown);
      yield delay(1000);
      const newCurrentValue = current - 1;
      if (newCurrentValue === 0) {
        yield put(setCountdown(false));
        yield put(setPlayPrompter(true));
      } else {
        yield put(setCurrentCountdown((current - 1)));
      }
    }
  }
}

function* stopCountdown() {
  const startDelay = yield select(selectStartDelayTime);
  yield put(setCountdown(false));
  yield put(setCurrentCountdown(startDelay));
  window.studioAudioService?.close();
}

function* scrollSpeed({ payload }: ReturnType<typeof setScrollSpeed>) {
  try {
    const intervalId = yield select(selectScrollIntervalId);
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      yield put(setScrollIntervalId(null));
    }
    const { data } = yield select(selectProject);
    if (data !== null) {
      saveSettingsByUser(data.user_owner_id.toString(), 'scrollSpeed', payload);
    }
  } catch (e) {
    console.log(e);
  }
}

function* openStudio() {
  try {
    yield put(setPlayPrompter(false));
    const { data } = yield select(selectProject);
    if (data !== null) {
      const settingsByOwnerUser = getSettingsByOwnerUser(data.user_owner_id.toString());
      yield put(setScrollSpeed(settingsByOwnerUser.scrollSpeed));
      yield put(setAutoScrollAudioService(settingsByOwnerUser.autoScrollAudioService));
    }
    yield put(setOpenStudio(true));
  } catch (e) {
    console.log(e);
  }
}

function* runningAudioService() {
  try {
    const isRunning = yield select(selectIsRunningAudioService);
    const isCountdown = yield select(selectIsCountdown);
    const studioMode = yield select(selectStudioMode);
    const isCameraMode = studioMode === StudioMode.CAMERA;
    const cameraId = yield select(selectCameraId);
    const isPrompterPlay = yield select(selectIsPlayPrompter);
    const cameraRecording = yield select(selectCameraRecording);
    const syncTeleprompter = yield select(selectTeleprompterSync);

    if ((window.studioAudioService && !isCountdown) || (isCameraMode && window.studioAudioService)) {
      if (isCameraMode) {
        if (isRunning) {
          if ((syncTeleprompter && cameraRecording) || isPrompterPlay) {
            window.studioAudioService.stopProcessingVoice();
            window.studioAudioService.start();
          } else {
            window.studioAudioService.stopProcessingVoice();
            window.studioAudioService.startProcessingVoiceCameraMode();
          }
        } else if (!cameraId) {
          window.studioAudioService.stopProcessingVoice();
          window.studioAudioService.close();
        } else {
          window.studioAudioService.stopProcessingVoice();
          window.studioAudioService.close();
          window.studioAudioService.startProcessingVoiceCameraMode();
        }
      } else if (isRunning) {
        window.studioAudioService.stopProcessingVoice();
        window.studioAudioService.start();
      } else {
        window.studioAudioService.stopProcessingVoice();
        window.studioAudioService.close();
      }
    }
  } catch (e) {
    console.log(e);
  }
}

function* scrollPosition({ payload }:ReturnType<typeof setScrollPosition>) {
  try {
    const intervalId = yield select(selectScrollIntervalId);
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      yield put(setScrollIntervalId(null));
    }
    yield put(setAutoScrolling(false));
    payload();
  } catch (e) {
    console.log(e);
  }
}

function* autoScrollAudioService({ payload }: ReturnType<typeof setAutoScrollAudioService>) {
  try {
    const isPlay = yield select(selectIsPlayPrompter);
    const isAutoScrollAudioService = yield select(selectAutoScrollAudioService);

    if (isPlay && isAutoScrollAudioService) {
      yield put(setAutoScrolling(false));
      yield put(setRunningAudioService(true));
    } else if (isPlay && !isAutoScrollAudioService) {
      yield put(setRunningAudioService(false));
      yield put(setAutoScrolling(true));
    }

    const { data } = yield select(selectProject);
    if (data !== null) {
      saveSettingsByUser(data.user_owner_id.toString(), 'autoScrollAudioService', payload);
    }
  } catch (e) {
    console.log(e);
  }
}


function guideStudio({ payload }: ReturnType<typeof setGuideStudio>) {
  try {
    saveSettings('guide', payload);
  } catch (e) {
    console.error(e);
  }
}

function fontSize({ payload }: ReturnType<typeof setFontSize>) {
  try {
    saveSettings('fontSize', payload);
  } catch (e) {
    console.error(e);
  }
}

function mediaNotAvailable({ payload }: ReturnType<typeof setMediaNotAvailable>) {
  try {
    saveSettings('mediaNotAvailable', payload);
  } catch (e) {
    console.error(e);
  }
}

function startDelayTime({ payload }: ReturnType<typeof setStartDelayTime>) {
  try {
    saveSettings('startDelayTime', payload);
  } catch (e) {
    console.error(e);
  }
}

function averageCalibrateVolume({ payload }: ReturnType<typeof setAverageCalibrateVolume>) {
  try {
    saveSettings('averageCalibrateVolume', payload);
  } catch (e) {
    console.error(e);
  }
}

function mainContainerHeight({ payload }: ReturnType<typeof setStudioMainContainerHeight>) {
  try {
    saveSettings('mainContainerHeight', payload);
  } catch (e) {
    console.error(e);
  }
}

function openSettingsPanel({ payload }: ReturnType<typeof setOpenSettingsPanel>) {
  try {
    saveSettings('isOpenSettingsPanel', payload);
  } catch (e) {
    console.error(e);
  }
}

function* toggleCameraRecord() {
  const startDelay = yield select(selectStartDelayTime) || 5;
  const isRecording = yield select(selectCameraRecording);
  const isCountdown = yield select(selectCountdownCamera);
  const isTeleprompterSync = yield select(selectTeleprompterSync);
  const cameraId = yield select(selectCameraId);
  const autoscrollAudioService = yield select(selectAutoScrollAudioService);

  if (cameraId) {
    if (startDelay) {
      if (isRecording) {
        yield put(setCameraRecording(false));
        if (isTeleprompterSync) {
          if (autoscrollAudioService) {
            yield put(setRunningAudioService(false));
          } else {
            yield put(setAutoScrolling(false));
          }
        } else {
          yield put(setAutoScrolling(false));
        }
      } else if (!isCountdown) {
        yield put(setRecordingAfterDelay(true));
      } else {
        if (isCountdown) {
          yield put(setCountdownCamera(false));
          yield put(setRecordingAfterDelay(false));
        }
        if (isTeleprompterSync) {
          if (autoscrollAudioService) {
            yield put(setRunningAudioService(false));
          } else {
            yield put(setAutoScrolling(false));
          }
        } else {
          yield put(setAutoScrolling(false));
        }
        yield put(setCountdownCamera(false));
      }
    } else {
      yield put(setCameraRecording(!isRecording));
      if (isTeleprompterSync) {
        if (autoscrollAudioService) {
          yield put(setRunningAudioService(!isRecording));
        } else {
          yield put(setAutoScrolling(!isRecording));
        }
      }
    }
  }
}

function* restartRecording() {
  yield put(toggleCameraRecording());
  yield delay(0);
  yield put(toggleCameraRecording());
}

function* startRecordingAfterDelay({ payload }: ReturnType<typeof setRecordingAfterDelay>) {
  const startDelay = yield select(selectStartDelayTime) || 5;
  const isTeleprompterSync = yield select(selectTeleprompterSync);
  const autoscrollAudioService = yield select(selectAutoScrollAudioService);
  if (payload) {
    yield put(setCountdownCamera(true));
    yield delay(startDelay * 1000);
    yield put(setCountdownCamera(false));
    yield put(setCameraRecording(true));
    if (isTeleprompterSync) {
      if (autoscrollAudioService) {
        yield put(setRunningAudioService(true));
      } else {
        yield put(setAutoScrolling(true));
      }
    }
  }
}

export default [
  takeLatest(setSetupAndTogglePrompter.type, setupAndTogglePrompter),
  takeLatest(setRestartPrompter.type, restartPrompter),
  takeLatest(setPlayPrompter.type, playPrompter),
  takeLatest(setScrollSpeed.type, scrollSpeed),
  takeLatest(setupStudio.type, openStudio),
  takeLatest(setRunningAudioService.type, runningAudioService),
  takeLatest(setScrollPosition.type, scrollPosition),
  takeLatest(setAutoScrollAudioService.type, autoScrollAudioService),
  takeLatest(setGuideStudio.type, guideStudio),
  takeLatest(setFontSize.type, fontSize),
  takeLatest(setMediaNotAvailable.type, mediaNotAvailable),
  takeLatest(setOpenSettingsPanel.type, openSettingsPanel),
  takeLatest(setStartDelayTime.type, startDelayTime),
  takeLatest(setAverageCalibrateVolume.type, averageCalibrateVolume),
  takeLatest(setStudioMainContainerHeight.type, mainContainerHeight),
  takeLatest(setPlayPrompterWithAudioCountdown.type, runAudioWithCountdown),
  takeLatest(setRunCountdown.type, runCountdownAndPlay),
  takeLatest(setStopCountdown.type, stopCountdown),
  takeLatest(toggleCameraRecording.type, toggleCameraRecord),
  takeLatest(setRecordingAfterDelay.type, startRecordingAfterDelay),
  takeLatest(restartRecordingCamera.type, restartRecording),
];
